import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Search, X, Trash2, Shield, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { PageShell } from '@/components/primitives/PageShell';
import { PageHeader } from '@/components/primitives/PageHeader';
import { DataList, type DataListColumn } from '@/components/primitives/DataList';
import { EmptyState } from '@/components/primitives/EmptyState';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { listFirefighters, deleteFirefighter } from '@/api/firefighters';
import { useTenant } from '@/contexts/TenantContext';
import { useAuth } from '@/contexts/AuthContext';

interface Firefighter {
  id: number;
  name: string;
  surname: string;
  note?: string | null;
  created_at?: string | null;
}

interface ListResponse {
  firefighters: Firefighter[];
  total: number;
  total_pages: number;
}

function initials(name: string, surname: string): string {
  return `${(name?.[0] || '').toUpperCase()}${(surname?.[0] || '').toUpperCase()}` || '?';
}

export default function FirefighterList() {
  const navigate = useNavigate();
  const { tenantId } = useTenant();
  const { can } = useAuth();
  const canCreate = can('firefighter', 'create');
  const canEdit = can('firefighter', 'update');
  const canDelete = can('firefighter', 'delete');
  const [items, setItems] = useState<Firefighter[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Firefighter | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, unknown> = { page, page_size: pageSize };
      if (search.trim()) params.search = search.trim();
      if (tenantId) params.tenant_id = tenantId;
      const data = (await listFirefighters(params)) as unknown as ListResponse;
      setItems(data.firefighters || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch (err) {
      console.error('Failed to list firefighters:', err);
      setError('Failed to load firefighters. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, tenantId]);

  useEffect(() => {
    const t = setTimeout(fetch, 200);
    return () => clearTimeout(t);
  }, [fetch]);

  const onDelete = async () => {
    if (!pendingDelete) return;
    const ff = pendingDelete;
    setPendingDelete(null);
    try {
      await deleteFirefighter(ff.id);
      toast.success(`${ff.name} ${ff.surname} deleted`);
      fetch();
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error('Failed to delete firefighter');
    }
  };

  const columns = useMemo<DataListColumn<Firefighter>[]>(
    () => [
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        accessorFn: (f) => `${f.name} ${f.surname}`,
        cell: (f) => (
          <div className="flex items-center gap-3">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-primary/10 text-primary text-xs font-medium">
                {initials(f.name, f.surname)}
              </AvatarFallback>
            </Avatar>
            <span className="font-medium">
              {f.name} {f.surname}
            </span>
          </div>
        ),
      },
      {
        id: 'note',
        header: 'Note',
        hideBelow: 'md',
        cell: (f) => (
          <span className="text-muted-foreground line-clamp-2">{f.note || '—'}</span>
        ),
      },
      {
        id: 'created',
        header: 'Added',
        hideBelow: 'lg',
        sortable: true,
        accessorFn: (f) => f.created_at,
        cell: (f) =>
          f.created_at ? new Date(f.created_at).toLocaleDateString() : '—',
      },
      {
        id: 'actions',
        header: '',
        cell: (f) => (
          <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
            {canEdit && (
              <Button
                size="icon"
                variant="ghost"
                onClick={() => navigate(`/firefighters/${f.id}/edit`)}
                title="Edit"
              >
                <Pencil className="h-4 w-4" />
              </Button>
            )}
            {canDelete && (
              <Button
                size="icon"
                variant="ghost"
                className="text-destructive hover:text-destructive"
                onClick={() => setPendingDelete(f)}
                title="Delete"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        ),
      },
    ],
    [navigate, canEdit, canDelete],
  );

  const mobileCard = (f: Firefighter) => (
    <Card>
      <CardContent className="flex items-start gap-3 p-4">
        <Avatar className="h-10 w-10">
          <AvatarFallback className="bg-primary/10 text-primary font-medium">
            {initials(f.name, f.surname)}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="font-medium">
            {f.name} {f.surname}
          </div>
          {f.note && (
            <div className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">
              {f.note}
            </div>
          )}
          <div className="mt-0.5 text-xs text-muted-foreground">
            Added{' '}
            {f.created_at ? new Date(f.created_at).toLocaleDateString() : '—'}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {canEdit && (
            <Button
              size="icon"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/firefighters/${f.id}/edit`);
              }}
              title="Edit"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          )}
          {canDelete && (
            <Button
              size="icon"
              variant="ghost"
              className="text-destructive hover:text-destructive"
              onClick={(e) => {
                e.stopPropagation();
                setPendingDelete(f);
              }}
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <PageShell>
      <PageHeader
        title="Firefighters"
        subtitle={total > 0 ? `${total} registered` : undefined}
        actions={
          canCreate ? (
            <Button asChild>
              <Link to="/firefighters/new">
                <Plus className="mr-1 h-4 w-4" />
                Add firefighter
              </Link>
            </Button>
          ) : undefined
        }
      />

      <div className="mb-4 max-w-sm">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search firefighters…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="pl-9 pr-9"
          />
          {search && (
            <button
              type="button"
              onClick={() => {
                setSearch('');
                setPage(1);
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {items.length === 0 && !loading ? (
        <EmptyState
          icon={Shield}
          title={search ? 'No firefighters match your search' : 'No firefighters yet'}
          description={
            search
              ? 'Try a different search term.'
              : 'Add the first firefighter to start tracking your team.'
          }
          action={
            search ? (
              <Button variant="outline" onClick={() => setSearch('')}>
                Clear search
              </Button>
            ) : canCreate ? (
              <Button asChild>
                <Link to="/firefighters/new">
                  <Plus className="mr-2 h-4 w-4" />
                  Add firefighter
                </Link>
              </Button>
            ) : undefined
          }
        />
      ) : (
        <DataList<Firefighter>
          data={items}
          columns={columns}
          mobileCard={mobileCard}
          loading={loading}
          getRowId={(f) => f.id}
          onRowClick={(f) => navigate(`/firefighters/${f.id}/edit`)}
          pagination={{
            page,
            pageSize,
            total,
            totalPages,
            onPageChange: setPage,
            onPageSizeChange: (n) => {
              setPageSize(n);
              setPage(1);
            },
          }}
        />
      )}

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete firefighter?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete && (
                <>
                  {pendingDelete.name} {pendingDelete.surname} will be removed from the
                  registry. This cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageShell>
  );
}
